import { Fragment, type KeyboardEvent } from "react";

import { useScrollOnMount } from "metabase/common/hooks/use-scroll-on-mount";
import type { BoxProps } from "metabase/ui";
import { Box } from "metabase/ui";

import type { ITreeNodeItem, TreeNodeComponent } from "./types";

interface TreeNodeListProps<TData = unknown> extends Omit<
  BoxProps,
  "children"
> {
  items: ITreeNodeItem<TData>[];
  expandedIds: Set<ITreeNodeItem<TData>["id"]>;
  selectedId?: ITreeNodeItem<TData>["id"];
  tabbableId?: ITreeNodeItem<TData>["id"];
  onFocusItem: (id: ITreeNodeItem<TData>["id"]) => void;
  depth: number;
  role?: string;
  onToggleExpand: (id: ITreeNodeItem<TData>["id"]) => void;
  onSelect?: (item: ITreeNodeItem<TData>) => void;
  TreeNode: TreeNodeComponent<TData>;
  rightSection?: (item: ITreeNodeItem<TData>) => React.ReactNode;
}

// Home/End, on the element that actually carries `role="tree"` (only the
// top-level list gets a `role` at all — see the comment below) — jumps to
// the first/last visible treeitem. ArrowUp/Down are handled per-item (see
// tree/utils.tsx's focusAdjacentTreeItem) but bubble up through this same
// element regardless, which also ensures a real keydown listener is
// attached to the exact node the "tree" role lives on, not just to
// descendants (WCAG 2.1.1, aria_keyboard_handler_exists).
function handleTreeKeyDown(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "Home" && event.key !== "End") {
    return;
  }

  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      '[role="treeitem"], [role="menuitem"]',
    ),
  );
  if (items.length === 0) {
    return;
  }

  event.preventDefault();
  const target = event.key === "Home" ? items[0] : items[items.length - 1];
  target.focus();
}

function BaseTreeNodeList<TData = unknown>({
  items,
  expandedIds,
  selectedId,
  tabbableId,
  onFocusItem,
  depth,
  onSelect,
  onToggleExpand,
  TreeNode,
  rightSection,
  role,
  ...boxProps
}: TreeNodeListProps<TData>) {
  const selectedRef = useScrollOnMount<HTMLLIElement>();

  return (
    // Stays <ul> at every level — a real <ul>'s direct children must be
    // <li>, and the top-level treeitems ARE direct children here, so
    // rendering the root as <div> orphans them (WCAG 1.3.1,
    // list_structure_proper flags that as a WORSE defect: verified via a
    // live re-scan that switching this to <div> traded 3 occurrences for
    // 126). role={role} overriding native "list" semantics is a standard,
    // valid ARIA pattern (e.g. <ul role="tablist">) and isn't itself a
    // violation — see SidebarCollectionLink.tsx / DropArea.tsx for the fix
    // that actually addresses this rule (role="presentation" on the
    // non-<li> wrappers between this <ul> and its treeitems).
    <Box
      component="ul"
      role={role}
      onKeyDown={role ? handleTreeKeyDown : undefined}
      {...boxProps}
    >
      {items.map((item) => {
        const isSelected = selectedId === item.id;
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0;
        const isExpanded = hasChildren && expandedIds.has(item.id);
        const onItemSelect =
          typeof onSelect === "function" ? () => onSelect(item) : undefined;
        const onItemToggle = () => onToggleExpand(item.id);

        return (
          <Fragment key={item.id}>
            <TreeNode
              ref={isSelected ? selectedRef : null}
              item={item}
              onSelect={onItemSelect}
              onToggleExpand={onItemToggle}
              isSelected={isSelected}
              isExpanded={isExpanded}
              hasChildren={hasChildren}
              isTabbable={tabbableId === item.id}
              onFocusItem={() => onFocusItem(item.id)}
              depth={depth}
              rightSection={rightSection}
            />
            {isExpanded && (
              <BaseTreeNodeList
                role="group"
                items={item.children!}
                expandedIds={expandedIds}
                selectedId={selectedId}
                tabbableId={tabbableId}
                onFocusItem={onFocusItem}
                depth={depth + 1}
                onSelect={onSelect}
                onToggleExpand={onToggleExpand}
                TreeNode={TreeNode}
                rightSection={rightSection}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

export const TreeNodeList = BaseTreeNodeList;
